import React, { useEffect, useState } from "react";
import { usePhrase } from "@/contexts/LanguageContext";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";

export default function FundingRequirements() {
  const pt = usePhrase();
  const [items, setItems] = useState([]);
  const [biz, setBiz] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ purpose: "Working capital", requested_amount: 1000000, preferred_tenure_months: 60, moratorium_months: 0, project_cost: 1250000, promoter_contribution: 250000, collateral_available: false });

  const load = async () => {
    const b = await api.get("/businesses"); setBiz(b.data[0]);
    const r = await api.get("/funding-requirements"); setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!biz) return toast.error(pt("Create business first"));
    setCreating(true);
    try {
      const { data } = await api.post("/funding-requirements", { ...form, business_id: biz.id });
      await api.post(`/funding-requirements/${data.id}/publish`);
      toast.success(pt("Funding requirement published"));
      await load();
    } catch (e) { toast.error(pt("Failed")); } finally { setCreating(false); }
  };

  return (
    <div className="space-y-6" data-testid="funding-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 brand-heading">{pt("Funding Requirements")}</h1>
        <p className="text-slate-600 mt-1 text-sm">{pt("Publish a requirement to trigger policy-based lender matching. You control who can discover you.")}</p>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 brand-heading">{pt("New requirement")}</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-800">{pt("Purpose")}</label>
            <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="fr-purpose" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2">
              {["Working capital", "Machinery or equipment", "Business expansion", "New business", "Loan takeover"].map((o) => <option key={o} value={o}>{pt(o)}</option>)}
            </select>
          </div>
          {[
            ["Requested amount (₹)", "requested_amount"],
            ["Preferred tenure (months)", "preferred_tenure_months"],
            ["Moratorium (months)", "moratorium_months"],
            ["Project cost (₹)", "project_cost"],
            ["Promoter contribution (₹)", "promoter_contribution"],
          ].map(([l, k]) => (
            <div key={k}>
              <label className="text-sm font-medium text-slate-800">{pt(l)}</label>
              <input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} data-testid={`fr-${k}`} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm mt-2 md:col-span-3">
            <input type="checkbox" checked={form.collateral_available} onChange={(e) => setForm({ ...form, collateral_available: e.target.checked })} data-testid="fr-collateral" />
            {pt("Collateral available")}
          </label>
        </div>
        <button onClick={create} disabled={creating} data-testid="fr-create-btn" className="mt-4 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60">{creating ? pt("Creating…") : pt("Publish requirement")}</button>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 brand-heading mb-4">{pt("My requirements")}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-2 pr-4">{pt("Purpose")}</th><th className="py-2 pr-4">{pt("Requested")}</th><th className="py-2 pr-4">{pt("Tenure")}</th><th className="py-2 pr-4">{pt("Status")}</th><th className="py-2 pr-4">{pt("Published")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100" data-testid={`fr-row-${it.id}`}>
                  <td className="py-3 pr-4 font-medium text-slate-900">{pt(it.purpose)}</td>
                  <td className="py-3 pr-4">{formatINR(it.requested_amount)}</td>
                  <td className="py-3 pr-4">{it.preferred_tenure_months || "—"} {pt("mo")}</td>
                  <td className="py-3 pr-4"><span className="rounded-full bg-slate-100 border px-2 py-0.5 text-xs">{pt(it.status)}</span></td>
                  <td className="py-3 pr-4">{it.published ? pt("Yes") : pt("No")}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-500">{pt("No funding requirements yet.")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <DisclaimerBanner compact />
    </div>
  );
}
